/**
 * VendorShipping.jsx — hybrid shipping setup for a storefront.
 * Manual lane (default): state a preferred carrier, paste tracking per order.
 * Automated lane: connect your own UPS/FedEx account through EasyPost for live rates.
 */
import { useState, useEffect } from "react";
import { Truck, Plug, Check, Loader, Trash2, AlertCircle, ExternalLink, Package } from "lucide-react";
import { apiFetch } from "./api";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700&family=IBM+Plex+Sans:wght@300;400;500;600&display=swap');
  .vs-root { --teal:#1a4a3a; --teal-mid:#2d6b54; --off-white:#f8f6f2; --border:#e4ddd4;
    --ink:#1a1a18; --ink-mid:#5a5a54; --ink-light:#9a9a90; --amber:#a06010; --amber-bg:#fef8ea;
    --green:#1a6a40; --green-bg:#eaf5f0; --red:#a02828;
    font-family:'IBM Plex Sans',sans-serif; background:var(--off-white); min-height:100vh; color:var(--ink); }
  .vs-head { background:#fff; border-bottom:1px solid var(--border); padding:20px 28px; }
  .vs-head h1 { font-family:'Syne',sans-serif; font-size:20px; color:var(--teal); margin:0 0 4px; }
  .vs-head p { font-size:13px; color:var(--ink-light); margin:0; }
  .vs-body { padding:20px 28px 48px; display:flex; flex-direction:column; gap:16px; max-width:820px; }
  .vs-card { background:#fff; border:1px solid var(--border); border-radius:14px; padding:20px; }
  .vs-card h2 { font-family:'Syne',sans-serif; font-size:15px; margin:0 0 4px; color:var(--teal); }
  .vs-card p.hint { font-size:12.5px; color:var(--ink-light); margin:0 0 16px; line-height:1.55; }
  .vs-lane { display:flex; gap:12px; flex-wrap:wrap; }
  .vs-lane-opt { flex:1; min-width:240px; border:1.5px solid var(--border); border-radius:12px; padding:14px;
    cursor:pointer; transition:border-color .15s, background .15s; background:var(--off-white); }
  .vs-lane-opt.active { border-color:var(--teal); background:#fff; box-shadow:0 2px 12px rgba(26,74,58,.08); }
  .vs-lane-opt strong { display:block; font-size:14px; margin-bottom:4px; }
  .vs-lane-opt span { font-size:12px; color:var(--ink-mid); line-height:1.5; }
  .vs-row { display:flex; gap:12px; flex-wrap:wrap; }
  .vs-field { flex:1; min-width:130px; }
  .vs-label { display:block; font-size:11px; font-weight:600; color:var(--ink-mid); margin-bottom:5px; }
  .vs-input { width:100%; background:var(--off-white); border:1.5px solid var(--border); border-radius:8px;
    padding:9px 12px; font-family:'IBM Plex Sans',sans-serif; font-size:13px; box-sizing:border-box; outline:none; }
  .vs-input:focus { border-color:var(--teal); background:#fff; }
  .vs-btn { display:inline-flex; align-items:center; gap:7px; padding:9px 18px; border-radius:8px; border:none;
    background:var(--teal); color:#fff; font-size:13px; font-weight:600; cursor:pointer; font-family:'IBM Plex Sans',sans-serif; }
  .vs-btn:hover { background:var(--teal-mid); }
  .vs-btn:disabled { opacity:.5; cursor:not-allowed; }
  .vs-btn.ghost { background:none; color:var(--ink-mid); border:1.5px solid var(--border); }
  .vs-acct { display:flex; align-items:center; gap:10px; padding:12px; border:1px solid var(--border);
    border-radius:10px; margin-bottom:8px; font-size:13px; background:var(--off-white); }
  .vs-badge { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.05em;
    padding:2px 8px; border-radius:8px; background:var(--green-bg); color:var(--green); }
  .vs-badge.pending { background:var(--amber-bg); color:var(--amber); }
  .vs-msg { display:flex; gap:8px; font-size:12.5px; padding:11px 13px; border-radius:10px; margin-top:12px; line-height:1.5; }
  .vs-msg.err { background:#fdf0f0; color:var(--red); }
  .vs-msg.ok { background:var(--green-bg); color:var(--green); }
  .vs-mode { font-size:11px; color:var(--ink-light); }
`;

const CARRIERS = ["USPS", "UPS", "FedEx", "DHL", "Other"];

export default function VendorShipping() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState(null);
  const [connect, setConnect]   = useState({ carrier: "UPS", account_number: "" });
  const [linkId, setLinkId]     = useState("");
  const [busy, setBusy]         = useState(false);
  const [origin, setOrigin]     = useState({ street: "", city: "", state: "", zip_code: "" });
  const [loadError, setLoadError] = useState(null);

  const load = () => {
    setLoadError(null);
    return apiFetch("/vendor/shipping/settings").then(res => {
      setSettings(res);
      const o = res.shipping_origin || {};
      setOrigin({ street: o.street || "", city: o.city || "", state: o.state || "", zip_code: o.zip_code || "" });
    }).catch(e => {
      // This used to swallow the error and leave the page spinning forever, which is what
      // the owner hit: "I only see a spinning wheel in the upper left hand corner."
      setLoadError(e.message || "We couldn't load your shipping settings.");
    });
  };
  useEffect(() => { load(); }, []);

  if (loadError) {
    return (
      <>
        <style>{styles}</style>
        <div className="vs-root">
          <div className="vs-head">
            <h1><Truck size={16} style={{ verticalAlign: -2, marginRight: 6 }} />Shipping</h1>
            <p>Where your parcels ship from, and how you buy postage.</p>
          </div>
          <div className="vs-body">
            <div className="vs-card" data-testid="shipping-load-error">
              <div className="vs-msg err" style={{ marginTop: 0 }}>
                <AlertCircle size={15} style={{ flexShrink: 0 }} />
                <span>{loadError}</span>
              </div>
              <p className="hint" style={{ marginTop: 14 }}>
                This usually means the storefront selected at the top of the Hub is no longer
                available — switch storefront, or reload, and it should come back.
              </p>
              <button className="vs-btn" onClick={load} data-testid="shipping-retry-btn">
                Try again
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!settings) {
    return (
      <>
        <style>{styles}</style>
        <div className="vs-root"><div className="vs-body"><Loader size={20} style={{ animation: "spin 1s linear infinite" }} /></div></div>
      </>
    );
  }

  const save = async (patch) => {
    setSaving(true); setMsg(null);
    try {
      const next = await apiFetch("/vendor/shipping/settings", {
        method: "PUT", body: JSON.stringify(patch),
      });
      setSettings(prev => ({ ...prev, ...next }));
      setMsg({ type: "ok", text: "Shipping settings saved." });
    } catch (e) {
      setMsg({ type: "err", text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const connectCarrier = async () => {
    if (!connect.account_number.trim()) { setMsg({ type: "err", text: "Enter your carrier account number" }); return; }
    setBusy(true); setMsg(null);
    try {
      const res = await apiFetch("/vendor/shipping/carrier-accounts/connect", {
        method: "POST", body: JSON.stringify(connect),
      });
      setMsg({
        type: "ok",
        text: res.authorization_url
          ? `${res.message} Open the authorisation link below to finish.`
          : res.message,
        url: res.authorization_url,
      });
      setConnect({ ...connect, account_number: "" });
      load();
    } catch (e) {
      setMsg({ type: "err", text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const linkCarrier = async () => {
    if (!linkId.trim()) return;
    setBusy(true); setMsg(null);
    try {
      await apiFetch("/vendor/shipping/carrier-accounts/link", {
        method: "POST", body: JSON.stringify({ carrier: connect.carrier, carrier_account_id: linkId.trim() }),
      });
      setLinkId("");
      setMsg({ type: "ok", text: "Carrier account linked." });
      load();
    } catch (e) {
      setMsg({ type: "err", text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async (id) => {
    setBusy(true);
    try {
      await apiFetch(`/vendor/shipping/carrier-accounts/${id}`, { method: "DELETE" });
      load();
    } catch (e) {
      setMsg({ type: "err", text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const parcel = settings.parcel_defaults || {};

  return (
    <>
      <style>{styles}</style>
      <div className="vs-root" data-testid="vendor-shipping-page">
        <div className="vs-head">
          <h1>Shipping</h1>
          <p>Choose how you ship. You can change this any time.</p>
        </div>

        <div className="vs-body">
          <div className="vs-card">
            <h2>How you ship</h2>
            <p className="hint">
              Ship it yourself and paste the tracking number, or connect your own carrier account so we can
              quote your real negotiated rates. Offering free shipping? Price it into your product — nothing
              to set up here.
            </p>
            <div className="vs-lane">
              <div
                className={`vs-lane-opt ${settings.lane === "manual" ? "active" : ""}`}
                onClick={() => save({ lane: "manual" })}
                data-testid="shipping-lane-manual"
              >
                <strong><Truck size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />I ship it myself</strong>
                <span>You buy postage wherever you like and paste the tracking number on each order.</span>
              </div>
              <div
                className={`vs-lane-opt ${settings.lane === "automated" ? "active" : ""}`}
                onClick={() => save({ lane: "automated" })}
                data-testid="shipping-lane-automated"
              >
                <strong><Plug size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />Use my carrier account</strong>
                <span>Connect UPS or FedEx below to see live rates on your orders.</span>
              </div>
            </div>

            <div className="vs-row" style={{ marginTop: 18 }}>
              <div className="vs-field" style={{ maxWidth: 200 }}>
                <label className="vs-label">Preferred carrier (shown to buyers)</label>
                <select
                  className="vs-input"
                  value={settings.preferred_carrier || ""}
                  onChange={e => save({ preferred_carrier: e.target.value })}
                  data-testid="shipping-preferred-carrier"
                >
                  <option value="">Not specified</option>
                  {CARRIERS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            {settings.shipping_origin?.city && (
              <p className="hint" style={{ margin: "14px 0 0" }}>
                Shipping from {settings.shipping_origin.city}, {settings.shipping_origin.state} {settings.shipping_origin.zip_code}
              </p>
            )}
          </div>

          <div className="vs-card">
            <h2>Ship-from address</h2>
            <p className="hint">
              Buyers can't get a shipping quote for your items until this is filled in — this is the
              address your parcels leave from.
            </p>
            <div className="vs-row">
              <div className="vs-field" style={{ minWidth: 220 }}>
                <label className="vs-label">Street</label>
                <input className="vs-input" value={origin.street} onChange={e => setOrigin({ ...origin, street: e.target.value })} data-testid="ship-from-street" />
              </div>
              <div className="vs-field">
                <label className="vs-label">City</label>
                <input className="vs-input" value={origin.city} onChange={e => setOrigin({ ...origin, city: e.target.value })} data-testid="ship-from-city" />
              </div>
              <div className="vs-field" style={{ maxWidth: 90 }}>
                <label className="vs-label">State</label>
                <input className="vs-input" value={origin.state} onChange={e => setOrigin({ ...origin, state: e.target.value.toUpperCase().slice(0, 2) })} data-testid="ship-from-state" />
              </div>
              <div className="vs-field" style={{ maxWidth: 130 }}>
                <label className="vs-label">ZIP</label>
                <input className="vs-input" value={origin.zip_code} onChange={e => setOrigin({ ...origin, zip_code: e.target.value })} data-testid="ship-from-zip" />
              </div>
            </div>
            <button className="vs-btn" style={{ marginTop: 14 }} onClick={() => save({ ship_from: origin })} disabled={saving} data-testid="save-ship-from-btn">
              <Check size={13} /> Save ship-from address
            </button>
            {!settings.shipping_origin?.street && (
              <div className="vs-msg err" data-testid="ship-from-warning">
                <AlertCircle size={15} />
                <span>No ship-from address saved yet — buyers currently can't see postage for your items.</span>
              </div>
            )}
          </div>

          <div className="vs-card">
            <h2>Default box</h2>
            <p className="hint">Used to quote rates when a product has no size or weight of its own.</p>
            <div className="vs-row">
              {[
                ["parcel_length", "Length (in)", parcel.length],
                ["parcel_width", "Width (in)", parcel.width],
                ["parcel_height", "Height (in)", parcel.height],
                ["parcel_weight_oz", "Weight (oz)", parcel.weight_oz],
              ].map(([field, label, value]) => (
                <div className="vs-field" key={field}>
                  <label className="vs-label">{label}</label>
                  <input
                    className="vs-input"
                    type="number"
                    defaultValue={value}
                    onBlur={e => {
                      const v = parseFloat(e.target.value);
                      if (v && v !== value) save({ [field]: v });
                    }}
                    data-testid={`shipping-${field}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="vs-card">
            <h2>Your carrier accounts <span className="vs-mode">({settings.mode} mode)</span></h2>
            <p className="hint">
              We never see or store your carrier password — the connection lives with our shipping partner and we
              only keep the account reference. Accounts connected in test mode have to be reconnected when we go live.
            </p>
            <p className="hint" style={{ marginTop: -8 }}>
              <strong>You don't need any of this to ship USPS.</strong> USPS postage is available on every order
              already — buy it on the order and the cost comes off that order's payout. Connect an account below
              only if you have negotiated UPS, FedEx or USPS rates you'd rather use.
            </p>

            {(settings.carrier_accounts || []).length === 0 ? (
              <p style={{ fontSize: 13, color: "var(--ink-light)", margin: "0 0 16px" }} data-testid="no-carrier-accounts">
                No carrier accounts connected yet.
              </p>
            ) : (
              settings.carrier_accounts.map(a => (
                <div className="vs-acct" key={a.carrier_account_id} data-testid={`carrier-account-${a.carrier}`}>
                  <Package size={15} color="var(--ink-light)" />
                  <strong>{a.carrier}</strong>
                  <span style={{ color: "var(--ink-light)", fontSize: 12 }}>{a.carrier_account_id}</span>
                  <span className={`vs-badge ${a.status === "active" ? "" : "pending"}`}>
                    {a.status === "active" ? "Connected" : "Awaiting authorisation"}
                  </span>
                  <button
                    className="vs-btn ghost"
                    style={{ marginLeft: "auto", padding: "6px 12px" }}
                    onClick={() => disconnect(a.carrier_account_id)}
                    disabled={busy}
                    data-testid="disconnect-carrier-btn"
                  >
                    <Trash2 size={13} /> Remove
                  </button>
                </div>
              ))
            )}

            <div className="vs-row" style={{ marginTop: 14, alignItems: "flex-end" }}>
              <div className="vs-field" style={{ maxWidth: 130 }}>
                <label className="vs-label">Carrier</label>
                <select
                  className="vs-input"
                  value={connect.carrier}
                  onChange={e => setConnect({ ...connect, carrier: e.target.value })}
                  data-testid="connect-carrier-select"
                >
                  <option>UPS</option>
                  <option>FedEx</option>
                  <option>USPS</option>
                </select>
              </div>
              <div className="vs-field">
                <label className="vs-label">Your account number with them</label>
                <input
                  className="vs-input"
                  value={connect.account_number}
                  onChange={e => setConnect({ ...connect, account_number: e.target.value })}
                  placeholder="e.g. 1A2B3C"
                  data-testid="connect-account-number"
                />
              </div>
              <button className="vs-btn" onClick={connectCarrier} disabled={busy} data-testid="connect-carrier-btn">
                {busy ? <Loader size={13} style={{ animation: "spin 1s linear infinite" }} /> : <Plug size={13} />} Connect
              </button>
            </div>

            <div className="vs-row" style={{ marginTop: 12, alignItems: "flex-end" }}>
              <div className="vs-field">
                <label className="vs-label">Already connected it elsewhere? Paste the account reference</label>
                <input
                  className="vs-input"
                  value={linkId}
                  onChange={e => setLinkId(e.target.value)}
                  placeholder="ca_..."
                  data-testid="link-carrier-input"
                />
              </div>
              <button className="vs-btn ghost" onClick={linkCarrier} disabled={busy || !linkId.trim()} data-testid="link-carrier-btn">
                <Check size={13} /> Link
              </button>
            </div>

            {msg && (
              <div className={`vs-msg ${msg.type === "ok" ? "ok" : "err"}`} data-testid="shipping-message">
                {msg.type === "ok" ? <Check size={15} /> : <AlertCircle size={15} />}
                <span>
                  {msg.text}
                  {msg.url && (
                    <>
                      {" "}
                      <a href={msg.url} target="_blank" rel="noreferrer" style={{ color: "inherit", fontWeight: 600 }}>
                        Authorise now <ExternalLink size={11} style={{ verticalAlign: "-1px" }} />
                      </a>
                    </>
                  )}
                </span>
              </div>
            )}
            {saving && <p className="vs-mode" style={{ marginTop: 10 }}>Saving…</p>}
          </div>
        </div>
      </div>
    </>
  );
}
